// EndpointSettings.tsx - Endpoint management section
import React, { useState } from 'react';
import { Button, Table, Space, message, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, BellOutlined } from '@ant-design/icons';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { useEffectiveAdminId } from '../../contexts/AdminSelectorContext';
import { EndpointModal } from './modals/EndpointModal';
import { NotificationConfigModal } from './modals/NotificationConfigModal';

export const EndpointSettings: React.FC = () => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data?: any;
  }>({ open: false, mode: 'create' });

  const [notificationModalState, setNotificationModalState] = useState<{
    open: boolean;
    endpointId: string | null;
    mode: 'create' | 'edit';
  }>({ open: false, endpointId: null, mode: 'create' });

  const { endpoints, deleteEndpoint } = useSuperAdmin();
  const effectiveAdminId = useEffectiveAdminId();

  // Filter endpoints based on effective admin ID
  const filteredEndpoints = effectiveAdminId === null
    ? endpoints // SuperAdmin viewing all
    : endpoints.filter(e => e.userId === effectiveAdminId);

  const handleCreate = () => {
    setModalState({ open: true, mode: 'create' });
  };

  const handleEdit = (endpoint: any) => {
    setModalState({ open: true, mode: 'edit', data: endpoint });
  };

  const handleDelete = (id: string) => {
    deleteEndpoint(id);
    message.success('Endpoint deleted successfully');
  };

  const handleManageNotifications = (endpointId: string) => {
    setNotificationModalState({ open: true, endpointId, mode: 'edit' });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      render: (url: string) => (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: 'Panel',
      key: 'panel',
      render: (record: any) => record.panel?.name || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Button
            icon={<BellOutlined />}
            onClick={() => handleManageNotifications(record.id)}
            size="small"
          >
            Notifications
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Endpoint
        </Button>
      </div>

      <Table
        dataSource={filteredEndpoints}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <EndpointModal
        open={modalState.open}
        mode={modalState.mode}
        initialData={modalState.data}
        onClose={() => setModalState({ open: false, mode: 'create' })}
      />

      {notificationModalState.endpointId && (
        <NotificationConfigModal
          open={notificationModalState.open}
          endpointId={notificationModalState.endpointId}
          mode={notificationModalState.mode}
          onClose={() => setNotificationModalState({ open: false, endpointId: null, mode: 'create' })}
        />
      )}
    </div>
  );
};
